import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { forwardRef } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BrandWordmark } from '@/components/ui/BrandWordmark';
import { palette } from '@/config/theme';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

export interface BragCardData {
  /** Display name shown under the avatar; falls back to the initial badge. */
  name: string;
  /** Remote avatar URL — omitted/half-loaded falls back to the initial. */
  avatarUrl?: string | null;
  /** Global leaderboard rank (1-based). */
  rank: number;
  /** Total prediction points. */
  points: number;
  /** Correct predictions count. */
  correct: number;
  /** Current daily streak (days). */
  streak: number;
}

/**
 * The "brag" card we rasterise to a PNG for the profile share action — a user
 * showing off their rank / streak any time, not tied to a single result. It is
 * the sibling of `ScoreShareCard` and follows the SAME capture contract:
 *
 *  - mounted OFF-SCREEN by the parent (absolute, far negative offset) so the
 *    user never sees it; `collapsable={false}` keeps the native view alive for
 *    `react-native-view-shot` to grab;
 *  - colours come from the STATIC dark `palette` (not the theme hook) so a
 *    shared image always looks like the signature Volt card even when the
 *    sharer is in light mode;
 *  - the avatar is the only network image; the parent should only mount this
 *    card once the avatar (if any) has had a chance to decode, exactly like the
 *    profile hero. A missing avatar degrades to the name initial — never a
 *    half-loaded logo bleeding into the export.
 *
 * Numbers are bidi-isolated in RTL the same way the rest of the app handles the
 * RN-web "Text forces LTR" gotcha.
 */
export const BRAG_CARD_WIDTH = 360;

export const BragCard = forwardRef<View, { data: BragCardData }>(function BragCard({ data }, ref) {
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const n = (x: number) => formatNumber(x, numeralOptions);

  // Top-three earns the gold treatment; everyone else stays on brand cyan.
  const podium = data.rank > 0 && data.rank <= 3;
  const accent = podium ? palette.captain : palette.voltBright;
  const hasStreak = data.streak >= 2;

  const initial = (data.name.trim().charAt(0) || '?').toUpperCase();

  return (
    <View
      ref={ref}
      collapsable={false}
      style={{
        width: BRAG_CARD_WIDTH,
        backgroundColor: palette.deep,
        borderRadius: 28,
        borderWidth: 1,
        borderColor: accent,
        paddingHorizontal: 24,
        paddingTop: 22,
        paddingBottom: 22,
        overflow: 'hidden',
      }}
    >
      {/* Header: wordmark + "my season" pill */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <BrandWordmark size={20} />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
            backgroundColor: palette.voltGlass,
            borderColor: palette.voltLine,
            borderWidth: 1,
            borderRadius: 999,
            paddingHorizontal: 10,
            paddingVertical: 4,
          }}
        >
          <Ionicons name="stats-chart" size={12} color={palette.voltBright} />
          <AppText variant="micro" weight="bold" style={{ color: palette.voltBright }}>
            {t('brag.kicker')}
          </AppText>
        </View>
      </View>

      {/* Identity: avatar + name */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <View
          style={{
            width: 60,
            height: 60,
            borderRadius: 30,
            borderWidth: 2,
            borderColor: accent,
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: palette.surface2,
          }}
        >
          {data.avatarUrl ? (
            <Image
              source={{ uri: data.avatarUrl }}
              style={{ width: 60, height: 60 }}
              contentFit="cover"
              cachePolicy="memory-disk"
            />
          ) : (
            <AppText variant="title" weight="bold" style={{ color: accent }}>
              {initial}
            </AppText>
          )}
        </View>
        <View style={{ flex: 1 }}>
          <AppText
            variant="heading"
            weight="bold"
            numberOfLines={1}
            style={{ color: palette.text }}
          >
            {data.name}
          </AppText>
          <AppText variant="caption" style={{ color: palette.muted }}>
            {t('brag.subtitle')}
          </AppText>
        </View>
      </View>

      {/* Hero rank */}
      <View
        style={{
          alignItems: 'center',
          backgroundColor: palette.surface2,
          borderRadius: 20,
          borderWidth: 1,
          borderColor: podium ? 'rgba(244,200,75,0.35)' : palette.voltLine,
          paddingVertical: 18,
          marginBottom: 14,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <Ionicons name="trophy" size={14} color={accent} />
          <AppText
            variant="micro"
            weight="bold"
            style={{ color: palette.soft, letterSpacing: 1.5 }}
          >
            {t('brag.rankLabel')}
          </AppText>
        </View>
        <AppText weight="bold" tabular style={{ color: accent, fontSize: 48, lineHeight: 54 }}>
          {`#${n(data.rank)}`}
        </AppText>
      </View>

      {/* Stat strip: points · correct · streak */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'stretch',
          borderTopWidth: 1,
          borderTopColor: palette.line,
          paddingTop: 14,
          marginBottom: 18,
        }}
      >
        <Stat label={t('brag.points')} value={n(data.points)} />
        <Divider />
        <Stat label={t('brag.correct')} value={n(data.correct)} />
        {hasStreak ? (
          <>
            <Divider />
            <Stat label={t('brag.streak')} value={`⁦${n(data.streak)} 🔥⁩`} highlight />
          </>
        ) : null}
      </View>

      {/* CTA */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <Ionicons name="download-outline" size={14} color={palette.voltBright} />
        <AppText variant="caption" weight="bold" style={{ color: palette.voltBright }}>
          {t('brag.cta')}
        </AppText>
      </View>
    </View>
  );
});

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <View style={{ flex: 1, alignItems: 'center', gap: 2 }}>
      <AppText
        weight="bold"
        tabular
        style={{ color: highlight ? palette.captain : palette.text, fontSize: 20 }}
      >
        {value}
      </AppText>
      <AppText
        variant="micro"
        weight="medium"
        style={{ color: palette.muted, letterSpacing: 0.5 }}
      >
        {label}
      </AppText>
    </View>
  );
}

function Divider() {
  return <View style={{ width: 1, backgroundColor: palette.line }} />;
}
